import { Component, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, catchError, tap } from 'rxjs';

import { AreaAtuacao } from '@core/models/area-atuacao';
import { AreaAtuacaoApiService } from '../services/area-atuacao-api.service';
import { FiltroTabelasAdministrativas } from '@tabelasAdministrativas/models/filtro';
import { UiService } from '@core/services/ui.service';

@Component({
	templateUrl: './tabela-area-atuacao.component.html',
	providers: [MessageService, ConfirmationService]
})
export class TabelaAreaAtuacaoComponent implements OnDestroy {
	pesquisarAoCarregar = true;
	habilitarFiltroCadastradoUsuario = true;
	filtros?: FiltroTabelasAdministrativas;
	limparFormularioPesquisa = false;
	resultado: any[] = [];
	loadingResultado = false;
	totalResultado?: number;
	pagina = 1;
	itensPorPagina = 10;
	primeiroItemResultado = 0;
	colunas: any;
	itemTabela = new AreaAtuacao();
	operacao?: string;
	exibirModalManterTabela = false;
	tituloModalManterTabela = '';
	subscriptions: Subscription[] = [];
	registrosSelecionados: AreaAtuacao[] = [];

	constructor(
		private confirmationService: ConfirmationService,
		private tabelaService: AreaAtuacaoApiService,
		private messageService: MessageService,
		private ui: UiService
	) {}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	prepararCadastrar() {
		this.tituloModalManterTabela = 'Cadastrar área de atuação';
		this.itemTabela = new AreaAtuacao();
		this.operacao = 'cadastrar';
		this.exibirModalManterTabela = true;
	}

	prepararAlterar(event: any) {
		this.subscriptions.push(
			this.tabelaService.obterAreaAtuacao(event.id).subscribe(res => {
				this.itemTabela = new AreaAtuacao(res.id, res.nome, res.ativo, res.incluido_usuario);
				this.tituloModalManterTabela = 'Alterar área de atuação';
				this.operacao = 'alterar';
				this.exibirModalManterTabela = true;
			})
		);
	}

	ativarInativarEmLote(ativar = true) {
		this.messageService.clear();
		if (this.registrosSelecionados.length === 0)
			return;

		const listaIdRegistrosSelecionados: number[] = [];
		for (const item of this.registrosSelecionados) {
			listaIdRegistrosSelecionados.push(item.id!);
		}
		const operacao = ativar ? 'ativada(s)' : 'inativada(s)';
		this.subscriptions.push(
			this.tabelaService
				.ativarInativarAreaAtuacao(listaIdRegistrosSelecionados!, ativar)
				.pipe(
					tap(() => {
						this.criarMensagem('success', `Área(s) de atuação ${operacao} com sucesso`);
						this.pesquisar();
					}),
					catchError(err => {
						this.criarMensagem('error', err.error.message);
						return [];
					})
				)
				.subscribe()
		);
	}

	salvarItem(event: AreaAtuacao) {
		const areaAtuacao = new AreaAtuacao();
		areaAtuacao.id = event.id;
		areaAtuacao.nome = event.nome;
		areaAtuacao.ativo = event.ativo ? true : false;
		areaAtuacao.incluido_usuario = event.incluido_usuario ? true : false;
		if (this.operacao === 'cadastrar') {
			this.salvarNovo(areaAtuacao);
		} else if (areaAtuacao.id) {
			this.alterar(areaAtuacao.id, areaAtuacao);
		}
		this.operacao = '';
		this.exibirModalManterTabela = false;
	}

	executarPesquisar(event: any) {
		this.filtros = new FiltroTabelasAdministrativas();
		this.filtros.nome = event.nome;
		this.filtros.ativo = event.ativo;
		this.filtros.incluido_usuario = event.incluido_usuario;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.messageService.clear();
		this.pesquisar();
		this.criarColunas();
	}

	limparFormulario() {
		if (this.pesquisarAoCarregar) {
			this.executarPesquisar(new FiltroTabelasAdministrativas());
			this.limparFormularioPesquisa = true;
		} else {
			this.resultado = [];
		}
	}

	excluirItem(event: any) {
		const areaAtuacao = event;
		this.confirmationService.confirm({
			message: 'Esta ação não poderá ser desfeita.',
			header: 'Deseja excluir a área de atuação ' + areaAtuacao.nome + '?',
			accept: async () => {
				this.tabelaService.excluirAreaAtuacao(event.id).subscribe(
					async (res: any) => {
						this.exibirMensagemSucesso(res);
					},
					err => {
						this.criarMensagem('error', err.error.message);
					}
				);
			}
		});
	}

	fecharModal() {
		this.exibirModalManterTabela = false;
		this.itemTabela = new AreaAtuacao();
		this.operacao = 'cadastrar';
	}

	paginar(event: any) {
		this.pagina = event.first / event.rows + 1;
		this.primeiroItemResultado = event.first;
		this.itensPorPagina = event.rows;
		this.pesquisar();
	}

	getItensPorPagina(event: any) {
		this.itensPorPagina = event.rows;
		
	}

	private pesquisar() {
		this.resultado = [];
		this.loadingResultado = true;
		if (this.filtros) {
			this.subscriptions.push(
				this.tabelaService.obterAreasAtuacao(this.filtros, this.pagina, this.itensPorPagina).subscribe(
					(resultado: any) => {
						this.loadingResultado = false;
						this.totalResultado = resultado.metadata.total;
						this.pagina = resultado.metadata.page;
						this.tratarDados(resultado.items);
					},
					err => {
						this.totalResultado = 0;
						this.resultado = [];
						this.criarMensagem('error', err.error.message);
					}
				)
			);
		}
	}

	private criarColunas() {
		this.colunas = [
			{ field: 'id', header: 'id' },
			{ field: 'nome', header: 'Área de atuação' },
			{ field: 'ativo', header: 'Ativo' },
			{ field: 'incluido_usuario', header: 'Cadastrado pelo usuário?' }
		];
	}

	private tratarDados(itens: any[]) {
		for (const item of itens) {
			const resultadoTratado = {
				id: item.id,
				nome: item.nome,
				ativo: item.ativo ? 'Sim' : 'Não',
				incluido_usuario: item.incluido_usuario ? 'Sim' : 'Não'
			};
			this.resultado.push(resultadoTratado);
		}
	}

	private salvarNovo(areaAtuacao: AreaAtuacao) {
		this.subscriptions.push(
			this.tabelaService.incluirAreaAtuacao(areaAtuacao).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private alterar(id: number, areaAtuacao: AreaAtuacao) {
		this.subscriptions.push(
			this.tabelaService.alterarAreaAtuacao(id, areaAtuacao).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private criarMensagem(criticidade: string, mensagem: string) {
		this.messageService.clear();
		this.messageService.add(this.ui.criarMensagem(criticidade, '', mensagem));
	}

	private exibirMensagemSucesso(res: any) {
		if (res) {
			this.criarMensagem('success', res.message);
			this.limparPesquisa();
		}
	}

	private limparPesquisa() {
		this.limparFormularioPesquisa = true;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.pesquisar();
	}
}
